import { useCallback, useState } from 'react';
import axios from '../axios-default';
import { useImmer } from 'use-immer';
import { MeditLinkCaseStatus } from '../model/oauth/medictLink_type';
import { STL_OPTIONS } from '../components/NewPatient/Photos/StlImpression_util';
import { useDispatch } from 'react-redux';
import { easySnackbar } from '../store/Noti/noti.actions';
import CaseRespondHandler from '../model/oauth/caseRespondHandler';
import CaseInfo from '../model/oauth/caseInfo';
import { CaseInfoRespond, PageInfo, PLATFORM_TYPE } from '../model/oauth/type';
import { useLang } from '../util/lang';

/**
 * @typedef {Object} OAuthFileUrlRespond
 * @property {string} uuid
 * @property {string} url
 * @property {string} downloadFileName
 */

export const OAuthFileUrlRespond = {};

/**
 * @typedef {Object} PlatformParamsOption
 * @property {string} access_token
 */

/**
 * @typedef {Object} OAuthModalState
 * @property {boolean} open
 * @property {string} title
 * @property {CaseInfo[]} caseInfos
 * @property {PLATFORM_TYPE} platform
 * @property {PageInfo} pageInfo
 * @property {string} access_token
 * @property {string} refresh_token
 */

/**
 * The auth object with its properties.
 * @type {OAuthModalState}
 */

export const OAuthModalState = {};
export const oAuthModalInitialState = {
    open: false,
    title: '',
    caseInfos: [],
    platform: '',
    pageInfo: {
        size: 0,
        page: 0,
        totalElements: 0,
        totalPage: 0,
        numberOfElements: 0,
    },
    access_token: '',
    refresh_token: '',
};

// may add more fields in the future
/**
 * @typedef {Object} SelectedOAuthStlTypes
 * @property {STL_OPTIONS} stlType
 * @property {string} fileId
 * @property {string} fileName
 * @property {string} url
 * @property {caseId} string
 */
export const SelectedOAuthStlTypes = {};
/**
 * @type {SelectedOAuthStlTypes[]}
 */
export const initselectedOAuthStlTypes = [];

export const OAUTH_BTN_ICON_MAP = {
    [PLATFORM_TYPE.MeditLink]: '/img/medit_logo.png',
    [PLATFORM_TYPE.PandaCloud_EU]: '/img/eletra_logo_colour.png',
    [PLATFORM_TYPE.PandaCloud_EN]: '/img/eletra_logo_colour.png',
};

export const PANDA_CLOUD_REGION_MAP = {
    [PLATFORM_TYPE.PandaCloud_EU]: 'eu',
    [PLATFORM_TYPE.PandaCloud_EN]: 'en',
};

export const BASE_CASE_RES = {
    caseData: [],
    pageInfo: {
        size: 0,
        page: 0,
        totalElements: 0,
        totalPage: 0,
        numberOfElements: 0,
    },
};

const useOAuthConnectPlatform = () => {
    const get = useLang('PatientInfo');
    const dispatch = useDispatch();
    const [modal, setModal] = useImmer(oAuthModalInitialState);
    const [oauthLoading, setOAuthLoading] = useState(false);
    const [selectedOAuthStlTypes, setSelectedOAuthStlTypes] = useImmer(initselectedOAuthStlTypes);

    /**
     * Disconnects a platform by its ID.
     * @type {(param:number) => Promise<void>} platformId - The ID of the platform to disconnect.
     */
    const disconnectPlatform = useCallback(async (platformId) => {
        const res = await axios.delete(`connectPlatform/disconnect/${platformId}`);
        if (res.data.success) {
            // console.log("success disconnect platform")
        }
    }, []);

    /**
     * Return platform dispalyName
     * @type {(platform:string) => string}
     */
    const getPlatformDisplayName = (platform) => {
        if (!platform || !platform.length) return '';
        switch (platform) {
            case PLATFORM_TYPE.MeditLink:
                return ' MeditLink ';
            case PLATFORM_TYPE.PandaCloud_EU:
                return ` Eletra (${get('OAuth.oauth_for_euro')}) `;
            case PLATFORM_TYPE.PandaCloud_EN:
                return ` Eletra (${get('OAuth.oauth_non_euro')}) `;
            default:
                return platform;
        }
    };

    /**
     * Fetches case data from a specified platform.
     *
     * @callback
     * @async
     * @param {PLATFORM_TYPE} platform - The platform to fetch data from. Currently only supports 'MeditLink'.
     * @param {PlatformParamsOption} option - The options for the request. Must include an 'access_token' property.
     * @returns {Promise<[]>}
     * @type {(platform:string , option:PlatformParamsOption) => Promise<CaseInfoRespond>}
     */
    const getCasesByPlatform = useCallback(
        async (platform, option) => {
            if (!platform || !platform.length || !option) throw new Error('Platform or option is not provided');
            setOAuthLoading(true);
            let baseRes = {
                caseData: [],
                pageInfo: {
                    size: 0,
                    page: 0,
                    totalElements: 0,
                    totalPage: 0,
                    numberOfElements: 0,
                },
            };
            try {
                const caseRespondHandler = new CaseRespondHandler(platform);
                let reqUrl = 'connectPlatform';
                switch (platform) {
                    case PLATFORM_TYPE.MeditLink:
                        reqUrl += `/MeditLink/cases/${option.access_token}`;
                        break;
                    case PLATFORM_TYPE.PandaCloud_EN:
                    case PLATFORM_TYPE.PandaCloud_EU:
                        const region = PANDA_CLOUD_REGION_MAP[platform];
                        reqUrl += `/Eletra/cases/${option.access_token}/${region}`;
                    default:
                        break;
                }
                if (reqUrl === 'connectPlatform') throw new Error('Platform is not supported');
                const res = await axios.get(reqUrl);
                if (res.status === 200 && res.data) {
                    baseRes.caseData = caseRespondHandler.handleCaseRespond(res.data.caseData);
                    baseRes.pageInfo = res.data.pageInfo;
                }
                return baseRes;
            } catch (error) {
                dispatch(easySnackbar(error.message, 'error'));
            } finally {
                setOAuthLoading(false);
            }
        },
        [dispatch, easySnackbar, setOAuthLoading]
    );

    /**
     * Fetches case data from a specified platform.
     * @callback
     * @async
     * @type {() => Promise<CaseInfoRespond>}
     */
    const showMoreRecord = async () => {
        setOAuthLoading(true);
        try {
            const caseRespondHandler = new CaseRespondHandler(modal.platform);
            let reqUrl = 'connectPlatform';
            switch (modal.platform) {
                case PLATFORM_TYPE.MeditLink:
                    reqUrl += `/MeditLink/cases/${modal.access_token}?page=${modal.pageInfo.page + 1}`;
                    break;
                case PLATFORM_TYPE.PandaCloud_EN:
                case PLATFORM_TYPE.PandaCloud_EU:
                    reqUrl += `/Eletra/cases/${modal.access_token}/${PANDA_CLOUD_REGION_MAP[modal.platform]}?page=${modal.pageInfo.page + 1}`;
                    break;
                default:
                    break;
            }
            if (reqUrl === 'connectPlatform') throw new Error('Platform is not supported');
            const res = await axios.get(reqUrl);
            if (res.data) {
                const { caseData, pageInfo } = res.data;
                if (caseData && pageInfo) {
                    const proccessedData = caseRespondHandler.handleCaseRespond(caseData);
                    setModal((draft) => {
                        draft.caseInfos = [...draft.caseInfos, ...proccessedData];
                        draft.pageInfo = pageInfo;
                    });
                }
            }
        } catch (error) {
            dispatch(easySnackbar(error.message, 'error'));
        } finally {
            setOAuthLoading(false);
        }
    };

    /**
     * @callback
     * @async
     * @type {(searchText:string) => string}
     */
    const handleMeditLinkSearchText = useCallback((searchText) => {
        if (!searchText || !searchText.length) return '';
        switch (searchText.toUpperCase()) {
            case MeditLinkCaseStatus.FORM:
            case MeditLinkCaseStatus.SCAN:
            case MeditLinkCaseStatus.NEED_PROCESSING:
            case MeditLinkCaseStatus.CAD:
            case MeditLinkCaseStatus.CAM:
            case MeditLinkCaseStatus.MILL:
            case MeditLinkCaseStatus.COMPLETED:
                return `status=${searchText.toUpperCase()}`;
            default:
                return `name=${searchText}`;
        }
    }, []);

    /**
     * @callback
     * @async
     * @type {(searchText:string, modal:OAuthModalState) => Promise<CaseInfoRespond>}
     */
    const searchCases = useCallback(
        async (searchText, modal) => {
            if (!modal.platform || !modal.platform.length || !searchText || !searchText.length) return;
            setOAuthLoading(true);
            setModal((draft) => {
                draft.caseInfos = [];
                draft.pageInfo = BASE_CASE_RES.pageInfo;
            });
            try {
                const caseRespondHandler = new CaseRespondHandler(modal.platform);
                let reqUrl = 'connectPlatform';
                switch (modal.platform) {
                    case PLATFORM_TYPE.MeditLink:
                        reqUrl += `/MeditLink/cases/${modal.access_token}?${handleMeditLinkSearchText(searchText)}`;
                        break;
                    case PLATFORM_TYPE.PandaCloud_EN:
                    case PLATFORM_TYPE.PandaCloud_EU:
                        const region = PANDA_CLOUD_REGION_MAP[modal.platform];
                        reqUrl += `/Eletra/cases/${modal.access_token}/${region}?keyword=${searchText}`;
                        break;
                    default:
                        break;
                }
                if (reqUrl === 'connectPlatform') throw new Error('Platform is not supported');
                const respond = await axios.get(reqUrl);
                if (respond.status === 200 && respond.data) {
                    const { caseData, pageInfo } = respond.data;
                    if (caseData && pageInfo) {
                        setModal((draft) => {
                            draft.caseInfos = caseRespondHandler.handleCaseRespond(caseData);
                            draft.pageInfo = pageInfo;
                        });
                    }
                }
            } catch (error) {
                dispatch(easySnackbar(error.message, 'error'));
            } finally {
                setOAuthLoading(false);
            }
        },
        [setOAuthLoading, dispatch, easySnackbar, setModal, handleMeditLinkSearchText]
    );

    /**
     *
     * @type {(platform:PLATFORM_TYPE) => void} validateOAuthSelectNecessaryStl, must have upper, lower, biteOne
     */
    const validateOAuthSelectNecessaryStl = (platform) => {
        const stlFiles = selectedOAuthStlTypes;
        let msgArray = [];
        const hasUpper = stlFiles.some((item) => item.stlType === STL_OPTIONS.upperFileName);
        const hasLower = stlFiles.some((item) => item.stlType === STL_OPTIONS.lowerFileName);
        const hasBiteOne = stlFiles.some((item) => item.stlType === STL_OPTIONS.biteOneFileName);
        switch (platform) {
            // only require upper for PandaCloud
            case PLATFORM_TYPE.PandaCloud_EN:
            case PLATFORM_TYPE.PandaCloud_EU:
                if (!hasUpper) {
                    msgArray.push(STL_OPTIONS.upperFileName);
                }
                break;
            default:
                [
                    { hasType: hasUpper, fileName: STL_OPTIONS.upperFileName },
                    { hasType: hasLower, fileName: STL_OPTIONS.lowerFileName },
                    { hasType: hasBiteOne, fileName: STL_OPTIONS.biteOneFileName },
                ].forEach((v) => {
                    if (!v.hasType) msgArray.push(v.fileName);
                });
                break;
        }
        if (msgArray.length > 0) {
            throw new Error(`Please select file: ${msgArray.join(', ')}`);
        }
    };

    /**
     *
     * @type {(param:SelectedOAuthStlTypes[] , access_token :string , platform : PLATFORM_TYPE) => Promise<OAuthFileUrlRespond[]>}
     */
    const getOAuthFileDownloadUrl = useCallback(
        async (stlFiles, access_token, platform) => {
            try {
                if (!stlFiles || !stlFiles.length) return [];
                if (!access_token || !access_token.length) throw new Error('Access token is not provided');
                if (!platform || !platform.length) throw new Error('Platform is not provided');
                const fileUuids = stlFiles.map((f) => f.fileId);
                let reqUrl = `connectPlatform`;
                switch (platform) {
                    case PLATFORM_TYPE.MeditLink:
                        reqUrl += `/MeditLink/cases/file/${access_token}`;
                        break;
                    case PLATFORM_TYPE.PandaCloud_EN:
                        reqUrl += `/Eletra/cases/file/${access_token}/en`;
                        break;
                    case PLATFORM_TYPE.PandaCloud_EU:
                        reqUrl += `/Eletra/cases/file/${access_token}/eu`;
                        break;
                    default:
                        break;
                }
                if (reqUrl === 'connectPlatform') throw new Error('Platform is not supported');
                const res = await axios.post(reqUrl, { fileUuids });
                if (res.status === 200 && res.data) {
                    return res.data.data;
                }
            } catch (error) {
                dispatch(easySnackbar(error.message, 'error'));
            }
        },
        [dispatch, easySnackbar]
    );

    return {
        modal,
        setModal,
        disconnectPlatform,
        getPlatformDisplayName,
        getCasesByPlatform,
        oauthLoading,
        selectedOAuthStlTypes,
        setSelectedOAuthStlTypes,
        showMoreRecord,
        searchCases,
        validateOAuthSelectNecessaryStl,
        setOAuthLoading,
        getOAuthFileDownloadUrl,
    };
};

export default useOAuthConnectPlatform;
